import React, { useState, useRef, lazy } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, ArrowRightIcon, StarIcon, AlertCircleIcon, LinkIcon, GitMergeIcon, MicIcon, HeadphonesIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useRoleTheme } from '../../hooks/useRoleTheme';
// Spice level labels + pill colors (word-only, no icons)
const spiceLevels: Record<number, {
  label: string;
  pillColor: string;
}> = {
  1: {
    label: 'Mild',
    pillColor: 'bg-sky-500/40 text-sky-200'
  },
  2: {
    label: 'Warm',
    pillColor: 'bg-amber-500/40 text-amber-200'
  },
  3: {
    label: 'Hot',
    pillColor: 'bg-orange-500/40 text-orange-200'
  },
  4: {
    label: 'Steamy',
    pillColor: 'bg-rose-500/40 text-rose-200'
  },
  5: {
    label: 'Scorching',
    pillColor: 'bg-red-500/40 text-red-200'
  }
};
// Darkness level labels + pill colors (word-only, no icons)
const darknessLevels: Record<number, {
  label: string;
  pillColor: string;
}> = {
  1: {
    label: 'Shadow',
    pillColor: 'bg-slate-400/30 text-slate-200'
  },
  2: {
    label: 'Grey',
    pillColor: 'bg-slate-500/30 text-slate-200'
  },
  3: {
    label: 'Abyss',
    pillColor: 'bg-gray-600/30 text-gray-200'
  },
  4: {
    label: 'Pitch Black',
    pillColor: 'bg-gray-700/40 text-gray-100'
  },
  5: {
    label: 'The Void',
    pillColor: 'bg-gray-800/50 text-white'
  }
};
const getSeriesStatusColor = (status: string): string => {
  const s = status?.toLowerCase() || '';
  if (s === 'complete' || s === 'backlist') return 'bg-green-600';
  if (s === 'ongoing') return 'bg-yellow-500';
  if (s.includes('hiatus') || s.includes('cancel') || s.includes('discontinu')) return 'bg-red-600';
  return 'bg-blue-600';
};
const getSeriesStatusLabel = (status: string): string => {
  const s = status?.toLowerCase() || '';
  if (s === 'complete' || s === 'backlist') return 'Complete';
  if (s === 'ongoing') return 'Ongoing';
  if (s.includes('hiatus')) return 'Hiatus';
  if (s.includes('cancel') || s.includes('discontinu')) return 'Discontinued';
  return status ? status.charAt(0).toUpperCase() + status.slice(1) : '';
};
const getGenreColor = (genre: string): string => {
  const g = genre.toLowerCase();
  if (g.includes('romance')) return 'bg-pink-600';
  if (g.includes('fantasy')) return 'bg-indigo-600';
  if (g.includes('sci-fi') || g.includes('science')) return 'bg-blue-600';
  if (g.includes('mystery')) return 'bg-indigo-600';
  if (g.includes('thriller')) return 'bg-red-600';
  if (g.includes('horror')) return 'bg-gray-800';
  if (g.includes('historical')) return 'bg-amber-600';
  if (g.includes('young adult') || g.includes('ya')) return 'bg-cyan-600';
  if (g.includes('contemporary')) return 'bg-emerald-600';
  return 'bg-violet-600';
};
const getBookCountBadgeColor = (status: string, hasPreorder?: boolean): string => {
  const s = status?.toLowerCase() || '';
  if (hasPreorder && s === 'ongoing') return 'bg-amber-500/80';
  if (s === 'complete' || s === 'backlist') return 'bg-green-600/80';
  if (s === 'ongoing') return 'bg-blue-500/80';
  if (s.includes('hiatus')) return 'bg-amber-500/80';
  if (s.includes('cancel') || s.includes('discontinu')) return 'bg-red-600/80';
  return 'bg-indigo-500/80';
};
interface AudiobookSeriesCarouselProps {
  series: any[];
  darkMode?: boolean;
}
const AudiobookSeriesCarousel: React.FC<AudiobookSeriesCarouselProps> = ({
  series,
  darkMode = false
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollLeft = () => {
    scrollRef.current?.scrollBy({
      left: -600,
      behavior: 'smooth'
    });
  };
  const scrollRight = () => {
    scrollRef.current?.scrollBy({
      left: 600,
      behavior: 'smooth'
    });
  };
  if (!series || series.length === 0) return null;
  return <div className="relative group" data-id="element-967" data-mp="3dc0vtdsvytto">
      <div ref={scrollRef} className="flex overflow-x-auto gap-3 sm:gap-4 pb-4 snap-x px-2 scrollbar-hide" style={{
      scrollbarWidth: 'none',
      msOverflowStyle: 'none'
    }} data-id="element-968" data-mp="3dd28tntgpqe1">
        {series.map(seriesItem => <AudiobookSeriesCard key={seriesItem.id} series={seriesItem} darkMode={darkMode} data-id="element-969" data-mp="3fqfufc6zxz4q" />)}
      </div>

      {/* Navigation arrows */}
      <button onClick={scrollLeft} className={`absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 ${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-full p-2 shadow-md opacity-0 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200 z-30`} aria-label="Scroll left" data-id="element-970" data-mp="3ordnizfwcm8j">
        <ChevronLeftIcon className={`h-6 w-6 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-971" data-mp="31106e3yuo7sq" />
      </button>
      <button onClick={scrollRight} className={`absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 ${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-full p-2 shadow-md opacity-0 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200 z-30`} aria-label="Scroll right" data-id="element-972" data-mp="35u2q7kdts2vq">
        <ChevronRightIcon className={`h-6 w-6 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-973" data-mp="25qlh5vkelgtd" />
      </button>
    </div>;
};
// Individual Audiobook Series Card — square (1:1) shape, image-only (no below-card text)
const AudiobookSeriesCard = ({
  series,
  darkMode
}: {
  series: any;
  darkMode: boolean;
}) => {
  const [imgError, setImgError] = useState(false);
  const rt = useRoleTheme();
  const coverImage = series.coverImage || series.books?.[0]?.coverImage || '';
  const showEndingType = (series.status === 'complete' || series.status === 'backlist') && series.endingType;
  const showPreOrder = series.status === 'ongoing' && series.preorderBook && !showEndingType;
  const publishedCount = series.books?.length || 0;
  const totalCount = series.totalBooks || publishedCount;
  const bookCountLabel = totalCount > 0 ? `${publishedCount}/${totalCount}` : `${publishedCount}`;
  const bookCountColor = getBookCountBadgeColor(series.status, !!series.preorderBook);
  const authorSlug = series.author?.toLowerCase().replace(/\s+/g, '-') || '';
  const narrators: string[] = series.narrators || [];
  const spice = series.spiceLevel && spiceLevels[series.spiceLevel];
  const darkness = series.darknessLevel && darknessLevels[series.darknessLevel];
  return <div className="flex-shrink-0 w-[calc(50%-6px)] sm:w-72 md:w-80 snap-start group/card relative" data-id="element-974" data-mp="187ek2vomg9it">
      <Link to={`/series/${series.id}`} className="block relative aspect-square rounded-lg overflow-hidden shadow-md transition-transform duration-200 group-hover/card:scale-105" data-id="element-975" data-mp="1d6hoqf0owdq9">
        {/* Cover image */}
        {imgError ? <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-indigo-100 via-indigo-200 to-indigo-300" data-id="element-976" data-mp="13f3jrrpyylpq">
            <span className="text-indigo-500 text-xs font-medium text-center px-2 line-clamp-3" data-id="element-977" data-mp="1vwpc9orwovlc">
              {series.name}
            </span>
          </div> : <img src={coverImage} alt={series.name} className="w-full h-full object-cover" loading="lazy" onError={() => setImgError(true)} data-id="element-978" data-mp="3gl9uybq39fhv" />}

        {/* ALWAYS VISIBLE */}

        {/* Top Left: Genre Tag */}
        {series.genres?.[0] && <span className={`absolute top-2 left-2 text-white text-xs font-medium ${getGenreColor(series.genres[0])} px-2 py-0.5 ${rt.badgeRadius} shadow-sm z-10`} data-id="element-979" data-mp="2xoie08y0rcgz">
            {series.genres[0]}
          </span>}

        {/* Top Right: Status Badge */}
        {series.status && <span className={`absolute top-2 right-2 text-white text-xs ${getSeriesStatusColor(series.status)} px-2 py-0.5 ${rt.badgeRadius} z-10`} data-id="element-980" data-mp="31plv0yrszejs">
            {getSeriesStatusLabel(series.status)}
          </span>}

        {/* Bottom Left: Content Warning Icon */}
        {series.contentWarnings && series.contentWarnings.length > 0 && <div className="absolute bottom-2 left-2 z-10" title={`Content Warnings: ${series.contentWarnings.join(', ')}`} data-id="element-981" data-mp="11069r1u1b9e9">
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-500 text-white shadow-md" data-id="element-982" data-mp="1a1w0e5g3e4az">
              <AlertCircleIcon className="h-3.5 w-3.5" data-id="element-983" data-mp="1evidfyq64a7b" />
            </span>
          </div>}

        {/* HOVER OVERLAY */}
        <div className="absolute inset-0 bg-black/85 backdrop-blur-sm opacity-0 group-hover/card:opacity-100 flex flex-col p-3 transition-opacity duration-200 text-white z-20" data-id="element-984" data-mp="1orbmv98lbnkv">
          <div className="min-w-0 flex-1 overflow-hidden" data-id="element-985" data-mp="1totzpapqamy5">
            {/* Series name + book count badge */}
            <div className="flex items-start justify-between gap-1 mb-0.5" data-id="element-986" data-mp="3vi1dugtluyig">
              <Link to={`/series/${series.id}`} className="font-bold text-white text-sm line-clamp-1 hover:text-indigo-300 transition-colors flex-1 min-w-0" onClick={e => e.stopPropagation()} data-id="element-987" data-mp="3vk4d7rgne3zu">
                {series.name}
              </Link>
              <span className={`flex-shrink-0 text-[10px] font-bold text-white px-1.5 py-0.5 rounded ${bookCountColor}`} data-id="element-988" data-mp="3n7kg3v1dinw9">
                {bookCountLabel}
              </span>
            </div>

            {/* Author */}
            <p className="text-white/80 text-xs line-clamp-1 mb-1.5" data-id="element-989" data-mp="esit8lh2x02u">
              by{' '}
              <Link to={`/author/${authorSlug}`} className="hover:text-indigo-300 hover:underline transition-colors" onClick={e => e.stopPropagation()} data-id="element-990" data-mp="9vk3sg8d1haz">
                {series.author}
              </Link>
            </p>

            {/* Connection type pill */}
            {series.connectionType && <div className="mb-1.5" data-id="element-991" data-mp="2lkb9xxnok7er">
                <span className={`inline-flex items-center ${rt.badgeRadius} border px-1.5 py-0.5 text-[10px] font-medium ${series.connectionType === 'connected' ? 'bg-teal-900/40 text-teal-200 border-teal-800' : 'bg-slate-800/60 text-slate-300 border-slate-700'}`} data-id="element-992" data-mp="2lj8my0thap44">
                  {series.connectionType === 'connected' ? <LinkIcon className="h-2.5 w-2.5 mr-1" data-id="element-993" data-mp="1njokdhg9cpia" /> : <GitMergeIcon className="h-2.5 w-2.5 mr-1" data-id="element-994" data-mp="2294b4xfsguxz" />}
                  {series.connectionType === 'connected' ? 'Connected' : 'Interconnected'}
                </span>
              </div>}

            {/* Narrators */}
            {narrators.length > 0 && <div className="flex items-center gap-1 mb-1 text-[11px] text-gray-300" data-id="element-995" data-mp="279ii0c5nlyuf">
                <MicIcon className="h-3 w-3 flex-shrink-0 text-indigo-400" data-id="element-996" data-mp="guzvvzrrugm3" />
                <span className="line-clamp-1" data-id="element-997" data-mp="cufwepjq70ws">{narrators.join(', ')}</span>
              </div>}

            {/* Total audio length */}
            {series.totalAudioLength && <div className="flex items-center gap-1 mb-1.5 text-[11px] text-gray-300" data-id="element-998" data-mp="6rl205wuj6es">
                <HeadphonesIcon className="h-3 w-3 flex-shrink-0 text-indigo-400" data-id="element-999" data-mp="3p5tz3tskw8r3" />
                <span data-id="element-1000" data-mp="3p3pwdf6f1fse">{series.totalAudioLength}</span>
              </div>}

            {/* Conditional tags row */}
            <div className="flex flex-wrap gap-1 mb-1.5" data-id="element-1001" data-mp="3f7w9p2yzg5ww">
              {showEndingType && <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-600/80 text-white" data-id="element-1002" data-mp="3k6hdtdhcv9qm">
                  {series.endingType}
                </span>}
              {showPreOrder && <Link to={`/book/${series.preorderBook.id}`} className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-500/80 text-white hover:bg-amber-500 transition-colors" onClick={e => e.stopPropagation()} data-id="element-1003" data-mp="3b59fwwir4ax2">
                  Pre-Order Bk {series.preorderBook.number}
                </Link>}
              {series.spinOffName && series.spinOffId && <Link to={`/series/${series.spinOffId}`} className="px-1.5 py-0.5 rounded text-[10px] font-medium text-fuchsia-300 bg-fuchsia-900/40 border border-fuchsia-800 hover:text-fuchsia-200 transition-colors flex items-center" onClick={e => e.stopPropagation()} data-id="element-1004" data-mp="130iq65oq1pnu">
                  {series.spinOffName}{' '}
                  <ArrowRightIcon className="h-2.5 w-2.5 ml-0.5" data-id="element-1005" data-mp="1lpp9nuxcalap" />
                </Link>}
              {series.contentWarnings && series.contentWarnings.length > 0 && <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-500/30 text-amber-200" title={`Content Warnings: ${series.contentWarnings.join(', ')}`} data-id="element-1006" data-mp="1bykx11v4zcj1">
                  <AlertCircleIcon className="h-3 w-3" data-id="element-1007" data-mp="2byxwfdsbqanc" />
                </span>}
            </div>

            {/* Tropes */}
            {series.tropes && series.tropes.length > 0 && <div className="flex flex-wrap gap-1 mb-1.5" data-id="element-1008" data-mp="2lxdllb7sj9u7">
                {series.tropes.slice(0, 3).map((trope: string) => <span key={trope} className="px-1.5 py-0.5 rounded text-[10px] bg-indigo-900/50 text-indigo-200 border border-indigo-800/50" data-id="element-1009" data-mp="2qx8bpcjqrve7">
                    {trope}
                  </span>)}
              </div>}
          </div>

          {/* Bottom: spice/darkness word pills + rating */}
          <div className="mt-auto pt-2 border-t border-white/10" data-id="element-1010" data-mp="39n7whn8juobt">
            <div className="flex items-center justify-between gap-1" data-id="element-1011" data-mp="39oiz3jq9dq1c">
              <div className="flex items-center gap-1 flex-wrap" data-id="element-1012" data-mp="3encghn8o216s">
                {spice && <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${spice.pillColor}`} data-id="element-1013" data-mp="3inwaihxy0oop">
                    {spice.label}
                  </span>}
                {darkness && <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${darkness.pillColor}`} data-id="element-1014" data-mp="syx8vc8m7sap">
                    {darkness.label}
                  </span>}
              </div>
              <div className="flex items-center text-amber-400 flex-shrink-0" data-id="element-1015" data-mp="18j0fvjdf86f">
                <StarIcon className="h-3.5 w-3.5 fill-current" data-id="element-1016" data-mp="3sfc210i2t9bh" />
                <span className="ml-1 text-xs font-bold text-white" data-id="element-1017" data-mp="a8ntf0nti412">
                  {series.rating}
                </span>
              </div>
            </div>
          </div>
        </div>
      </Link>
      {/* No below-card text — image only */}
    </div>;
};
export default AudiobookSeriesCarousel;